import { assertGraphCapture } from '@/simulation/native/authority';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { ILogicGraph } from '@/interfaces/models';
import type { LogicEdgeDto, LogicGraphDto, LogicNodeInstanceDto } from '@/schemas/logic';
import { cloneNodeSavedState } from '@/models/logic/LogicNodeInstance';
import { nodeSnapshotWallAt } from '@/models/logic/SavedNodeState';
import { jsonChunks, type JsonChunkOptions } from '@/utils/jsonChunks';

/**
 * Replayable read view, not an immutable snapshot. Consume all captures/fallback
 * synchronously in the caller's same coherent epoch. Never retain this view in
 * a worker baseline or yield to another simulation tick while capturing it.
 */
export interface GraphRecordSource {
  readonly nodeCount: number;
  nodes(): Iterable<LogicNodeInstanceDto>;
  nativeNodes?(): Iterable<NativeNodeRecord>;
  edges(): Iterable<LogicEdgeDto>;
}

/** Scalar identity components from a trusted native capture, not import handles. */
export type NativeNodeRecord =
  | LogicNodeInstanceDto
  | (Omit<LogicNodeInstanceDto, 'id'> & {
      id: number;
      idPrefix: string;
    });

const nativeSources = new WeakSet<GraphRecordSource>();
export function hasOwnedNativeRecords(source: GraphRecordSource): boolean {
  return nativeSources.has(source);
}

/**
 * Native graph/node DTO semantics only. Selection comes from the owner plan.
 * Each emitted record is a fresh owned object, never an alias of a live node.
 */
export function liveGraphRecords(
  graph: ILogicGraph,
  retainedIds?: readonly NodeHandle[],
): GraphRecordSource {
  assertGraphCapture(graph);
  const wallAt = nodeSnapshotWallAt();
  const retained = retainedIds ? [...retainedIds] : undefined;
  const selected = retained ? new Set(retained) : undefined;
  if (selected && selected.size !== retained!.length)
    throw new Error('Duplicate retained node ID');
  if (selected)
    for (const id of selected)
      if (!graph.nodes.has(id)) throw new Error('Missing retained node');
  const source: GraphRecordSource = {
    get nodeCount() {
      return selected?.size ?? graph.nodes.size;
    },
    *nodes() {
      for (const node of graph.boundaryNodes(retained)) {
        // Mirror LogicNodeInstance.dto, cloning state together with the output.
        // Only this one node record exists here, not an array for the graph.
        yield {
          id: node.idText,
          type: node.type,
          state: cloneNodeSavedState(node, wallAt),
          output: node.output,
          runtimeKey: node.runtimeKey,
        };
      }
    },
    *nativeNodes() {
      for (const node of graph.boundaryNodes(retained)) {
        if (node.hasImportedIdentity)
          yield {
            id: node.idText,
            type: node.type,
            state: cloneNodeSavedState(node, wallAt),
            output: node.output,
            runtimeKey: node.runtimeKey,
          };
        else
          yield {
            id: node.id,
            idPrefix: node.ids.nodePrefix,
            type: node.type,
            state: cloneNodeSavedState(node, wallAt),
            output: node.output,
            runtimeKey: node.runtimeKey,
          };
      }
    },
    *edges() {
      for (const edge of graph.edges.values())
        if (
          !selected ||
          (selected.has(edge.sourceNodeId) && selected.has(edge.targetNodeId))
        )
          yield graph.edgeDto(edge);
    },
  };
  Object.freeze(source);
  nativeSources.add(source);
  return source;
}

/** Exact equality of canonical streams, independent of record/block chunking. */
export function equalSnapshotChunks(
  left: Iterable<string>,
  right: Iterable<string>,
): boolean {
  const steps = equalSnapshotChunkSteps(left, right);
  let result = steps.next();
  while (!result.done) result = steps.next();
  return result.value;
}

/** Cooperative canonical comparison; chunk packing never changes equality. */
export function* equalSnapshotChunkSteps(
  left: Iterable<string>,
  right: Iterable<string>,
): Generator<void, boolean> {
  let a: Iterator<string> | undefined, b: Iterator<string> | undefined;
  try {
    a = left[Symbol.iterator]();
    b = right[Symbol.iterator]();
    let x = a.next(),
      y = b.next(),
      xi = 0,
      yi = 0;
    while (true) {
      while (!x.done && xi === x.value.length) {
        x = a.next();
        xi = 0;
      }
      while (!y.done && yi === y.value.length) {
        y = b.next();
        yi = 0;
      }
      if (x.done || y.done) return !!x.done && !!y.done;
      const length = Math.min(x.value.length - xi, y.value.length - yi);
      if (x.value.slice(xi, xi + length) !== y.value.slice(yi, yi + length)) return false;
      xi += length;
      yi += length;
      yield;
    }
  } finally {
    try {
      a?.return?.();
    } finally {
      b?.return?.();
    }
  }
}

/** Existing-file adapter. Ownership of the input still belongs to its caller. */
export function legacyGraphRecords(graph: LogicGraphDto): GraphRecordSource {
  return {
    get nodeCount() {
      return graph.nodes.length;
    },
    *nodes() {
      yield* graph.nodes;
    },
    *edges() {
      yield* graph.edges;
    },
  };
}

/**
 * Bounded record traversal for a future immutable compact reader. Also useful
 * for synchronous adapter comparisons. No full decoded graph is constructed.
 * jsonChunks retains its existing scalar escaping/overflow behavior.
 */
export function* graphJsonChunks(
  source: GraphRecordSource,
  options: Pick<JsonChunkOptions, 'canonical' | 'strict' | 'chunkCharacters'> = {},
): Generator<string> {
  const fields = options.canonical
    ? (['edges', 'nodes'] as const)
    : (['nodes', 'edges'] as const);
  yield '{';
  for (let index = 0; index < fields.length; index++) {
    const field = fields[index];
    yield `${index ? ',' : ''}"${field}":[`;
    let first = true;
    for (const record of field === 'nodes' ? source.nodes() : source.edges()) {
      if (!first) yield ',';
      first = false;
      yield* jsonChunks(record, options);
    }
    yield ']';
  }
  yield '}';
}
